/* global globalThis */
import URL from "./apiURL";
import { authenticatedFetch } from "../../utils/authenticatedFetch";
import { getValidToken } from "../../utils/getValidToken";

const postBlobWithAuth = async (url, body) => {
  const token = await getValidToken();
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorMessage = `Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.message || errorData?.error || errorMessage;
    } catch {
      // Ignore non-JSON error body.
    }
    throw new Error(errorMessage);
  }

  return await response.blob();
};


export async function getMasterData(type, userEmail) {
  try {
    return await authenticatedFetch(`${URL.getMasterApi}/${type}/${userEmail}`, {
      method: "GET",
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function getInterlinkagesMasterData(type, requestObj) {
  try {
    return await authenticatedFetch(`${URL.getInterlinkageRecords}/${type}`, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}
/** 
  *  * @param {email} email 
  *    @param {type} type
*/

export async function getOptimizedFilterDetails(type, email) {
  const requestObj = {
    "type": type,
    "email": email
  };

  try {
    return await authenticatedFetch(`${URL.getOptimizedFilterDetails}`, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function getAlertSummaryFilters(email, filters = {}, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummaryFilters, {
      method: "POST",
      body: JSON.stringify({ email, filters, type }),
    });
  } catch (error) {
    console.error("Error fetching alert summary filters:", error.message);
    throw error;
  }
}

export async function getAlertSummaryData(email, filters = {}, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummaryData, {
      method: "POST",
      body: JSON.stringify({ email, filters, type }),
    });
  } catch (error) {
    console.error("Error fetching alert summary data:", error.message);
    throw error;
  }
}

export async function getAlertSummaryKPIData(email, filters = {}, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummaryKPIData, {
      method: "POST",
      body: JSON.stringify({ email, filters, type}),
    });
  } catch (error) {
    console.error("Error fetching alert summary kpi data:", error.message);
    throw error;
  }
}

export async function getAlertSummarySiteAdaptionData(email, filters = {}, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummarySiteAdaptionData, {
      method: "POST",
      body: JSON.stringify({ email, filters, type }),
    });
  } catch (error) {
    console.error("Error fetching alert summary site adaption data:", error.message);
    throw error;
  }
}

export async function getAlertSummaryChartData(email, filters = {}, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummaryChartData, {
      method: "POST",
      body: JSON.stringify({ email, filters, type }),
    });
  } catch (error) {
    console.error("Error fetching alert summary chart data:", error.message);
    throw error;
  }
}

export async function getAlertSummaryTrendsData(email, filters = {}, showWeekly=true, type = "RMPMSummary") {
  try {
    return await authenticatedFetch(URL.alertSummaryTrendsData, {
      method: "POST",
      body: JSON.stringify({ email, filters, showWeekly, type }),
    });
  } catch (error) {
    console.error("Error fetching alert summary trends data:", error.message);
    throw error;
  }
}

/**
 * Function to call landing API
 * @returns 
 */
export async function landingAPI() {
  try {
    return await authenticatedFetch(URL.landingApi, {
      method: "POST",
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Alert Details
 *  * @param {email} email 
 * @param {type} type 

 * @returns 
 */
export async function alertDetailAPI(requestPayload) {
  try {
    // Determine which endpoint to use based on type
    let endpoint;
    if (requestPayload.type === 'RMPM Alert') {
      endpoint = URL.alertDetailApi;
    } else if (requestPayload.type === 'MAI') {
      endpoint = URL.maiAPI;
    } else if (requestPayload.type === 'OpenPO Details') {
      endpoint = URL.openPOAPI;
      requestPayload = {
        ...requestPayload,
        groupByMaterialId: requestPayload.groupByMaterialId === true,
      };
    } else if (requestPayload.type === 'Norm Alert') {
      endpoint = URL.normAlert;
    } else {
      endpoint = URL.fgCoverageAPI;
    }
    
    return await authenticatedFetch(endpoint, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Demand Supply API
 * @param {Alert Id} alertId 
 * @returns 
 */
export async function demandSupplyAPI(alertId) {

  const requestObj = {
    "Alert Id": alertId
  };

  try {
    return await authenticatedFetch(URL.demandDetailApi, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Key Insight API
 * @param {Material Number} materialId 
 * @param {Plant Code} plantCode
 * @param {Week} week
 * @param {Alert Id} alertId
 * @returns 
 */
export async function keyInsightApi(materialId, plantCode, week, alertId) {

  const requestObj = {
    "Material Number": materialId,
    "Plant Code": plantCode,
    "week": week,
    "alertId": alertId
  }
  try {
    return await authenticatedFetch(URL.keyInsight, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}


/**
 * Function to call Supplier Insights Details API
 */
export async function supplierInsightsAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.supplierInsightsAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Vendor OTIF API
 */
export async function vendorOtifAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.vendorOtifAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Vendor QC API 
 */
export async function vendorQCAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.vendorQCAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Overdue PO API 
 */
export async function overduePOAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.overduePOAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Lead Time Analysis API 
 */
export async function leadTimeAnalysisAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.leadTimeAnalysisAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call SOB Compliance API 
 */
export async function sobComplianceAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.sobComplianceAPI, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call FG Coverage Alert API
 * @returns 
 */
export async function fgCoverageAPI(requestObj) {

  try {
    return await authenticatedFetch(URL.fgCoverageAPI, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call execute Action API
 * @param {Alert Id} alertIds
 * @param {Alert Status} status
 * @param {Alert Reason} reason
 * @returns 
*/
export async function executeActionAPI(alertIds, status, reason = '', type = '', alertDetails = [], username = '') {
  const requestObj = {
    type: type,
    alertIds: alertIds,
    status: status,
    reason: reason,
    actionAlertDetails: alertDetails,
    username: username
  }

  try {
    return await authenticatedFetch(URL.executeAction, {
      method: "POST",
      body: JSON.stringify(requestObj),
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call export to excel Supplier Insights API
 * @param {Table data} tableData
 * @returns 
*/

export async function exportToExcelSupplierFile(requestPayload) {

  try {
    const blob = await postBlobWithAuth(URL.exportSupplierExcel, requestPayload);

    const url = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "exported_data.xlsx");
    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }

}

/**
 * Function to call export to excel API
 * @param {Table data} tableData
 * @returns 
*/

export async function exportToExcelFile(tableData, type) {

  const requestObj = {
    table_data: tableData,
  }

  try {
    const blob = await postBlobWithAuth(
      type === 'CBU_line_table' ? URL.cbu_line_level_export : URL.export,
      requestObj
    );

    const url = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "line_cbu_level_exported_data.xlsx");
    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }

}

/**
 * Function to call export to excel API
 * @param {Screen Type} type
 * @param {Email} email
 * @returns 
*/


export async function exportExcelFile(type, email) {
  const today = new Date().toLocaleDateString();

  const requestObj = {
    type: type,
    email: email
  }

const viewTypeMap = {
  'RMPM Alert':'Detailed View',
  'MAI': 'MAI View',
  'FG Coverage': 'FG Coverage',
  'OpenPO Details': 'Open PO Details',
  'Norm Alert': 'Stock to Norm',
  'Contract Alert': 'Contract Current week',
  'Contract PR2 Alert':'Contract Future week'
};

const typePage=type?.type;
const viewType = viewTypeMap[typePage] ?? typePage;

  try {
    const blob = await postBlobWithAuth(URL.exportExcel, requestObj);

    const url = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${viewType}_${today}.xlsx`);
    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Norm P2 Alert API
 * @returns 
 */
export async function normDetailsAPI(requestPayload) {

  try {
    return await authenticatedFetch(URL.normAlert, {
      method: "POST",
      body: JSON.stringify(requestPayload)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}


/**
 * Function to call Save Filter Preference API
 * @returns 
 * @param {email} email 
 * @param {type} type 
 * @param {filters} filters
 */
export async function saveFilterPreference(email, type, filters) {

  const validatedFilters = Array.isArray(filters) ? filters : [];

  const requestObj = {
    "email": email,
    "type": type,
    "filters": validatedFilters,
  };

  try {
    return await authenticatedFetch(URL.saveFilterPreferences, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/**
 * Function to call Save Column Preference API
 * @returns 
 * @param {email} email 
 * @param {type} type 
 * @param {column} column
 */
export async function saveColumnPreference(email, type, column) {

  const validatedColumn = Array.isArray(column) ? column : [];

  const requestObj = {
    "email": email,
    "type": type,
    "filters": validatedColumn,
  };

  try {
    return await authenticatedFetch(URL.saveColumnPreferences, {
      method: "POST",
      body: JSON.stringify(requestObj)
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

/** Fetch a user's saved/default table-column preferences. */
export async function getColumnPreferences(email, type) {
  try {
    return await authenticatedFetch(URL.customizeColumns, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, type }),
    });
  } catch (error) {
    console.error("Error fetching column preferences:", error.message);
    throw error;
  }
}
export async function exportExcelAgileORInsight(payload) {

  const requestObj = payload

  try {
    const blob = await postBlobWithAuth(URL.exportExcelAgileORInsight, requestObj);

    const url = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${payload?.type?.replace(' ', '_')}_exported_data.xlsx`);
    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function getPODetailsMAIPage(payload) {
  try {
    return await authenticatedFetch(URL.getPODetails, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}
